const HEADER_OFFSET = 120
const DRIFT_TOLERANCE_PX = 4
const CORRECT_FRAMES_REQUIRED = 6
// Safety cap so a target that never appears (bad id, typo), or that never
// settles, doesn't poll forever.
const MAX_FRAMES = 240 // ~4s at 60fps

// Single source of truth for scrolling to an in-page anchor, used for both
// same-page clicks (header.js) and cross-page navigation (gatsby-browser.js).
//
// Two things fight over scroll position right after navigating to a URL
// with a hash: the browser's own native anchor-scroll (which ignores our
// fixed header and jumps the target flush to the viewport top), and page
// content still loading in (images, web fonts, Sanity-sourced tables),
// which keeps shifting where the target actually ends up. A single
// corrective scrollTo() is not enough — the browser can re-apply its own
// native jump afterward, undoing the correction.
//
// Instead, run a per-frame loop for a bounded window: every frame, measure
// the target's position and, if it's off by more than a few px, snap it
// back with an instant (non-animated) scrollTo. Using "auto" instead of
// "smooth" means there's no in-flight animation to race against on the next
// frame. Stop once the position has been correct for several consecutive
// frames in a row, or the time budget runs out. Back off permanently the
// moment the user scrolls manually.
export function scrollToAnchor(id) {
  if (typeof document === "undefined" || !id) return

  let frame = 0
  let correctStreak = 0
  let userScrolled = false

  const onUserScroll = () => {
    userScrolled = true
  }
  window.addEventListener("wheel", onUserScroll, { passive: true })
  window.addEventListener("touchmove", onUserScroll, { passive: true })
  window.addEventListener("keydown", onUserScroll)

  const cleanup = () => {
    window.removeEventListener("wheel", onUserScroll)
    window.removeEventListener("touchmove", onUserScroll)
    window.removeEventListener("keydown", onUserScroll)
  }

  const tick = () => {
    if (userScrolled) {
      cleanup()
      return
    }

    const el = document.getElementById(id)
    if (!el) {
      if (frame++ < MAX_FRAMES) {
        window.requestAnimationFrame(tick)
      } else {
        cleanup()
      }
      return
    }

    const top = el.getBoundingClientRect().top
    const drift = top - HEADER_OFFSET

    if (Math.abs(drift) > DRIFT_TOLERANCE_PX) {
      const targetY = Math.max(0, window.pageYOffset + drift)
      window.scrollTo({ top: targetY, behavior: "auto" })
      correctStreak = 0
    } else {
      correctStreak += 1
    }

    frame += 1
    if (correctStreak >= CORRECT_FRAMES_REQUIRED || frame >= MAX_FRAMES) {
      cleanup()
      return
    }

    window.requestAnimationFrame(tick)
  }

  window.requestAnimationFrame(tick)
}
